if (!customElements.get('product-quantity-selector')) {
  customElements.define(
    'product-quantity-selector',
    class ProductQuantitySelector extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.minusButton = this.querySelector('button[name="minus"]');
        this.plusButton = this.querySelector('button[name="plus"]');
        this.productFormId = this.dataset.formId;
        this.sectionId = this.dataset.section;
        this.changeEvent = new Event('change', { bubbles: true });
        this.variantChangeUnsubscriber = null;
        
        // Inicializa os event listeners
        this.setupEventListeners();
        
        // Sincroniza com outros seletores de quantidade na página
        this.syncWithOtherQuantitySelectors();
      }

      setupEventListeners() {
        if (this.minusButton) {
          this.minusButton.addEventListener('click', this.onMinusClick.bind(this));
        }
        
        if (this.plusButton) {
          this.plusButton.addEventListener('click', this.onPlusClick.bind(this));
        }
        
        if (this.input) {
          this.input.addEventListener('change', this.onInputChange.bind(this));
        }
        
        // Registra evento de mudança de variante
        if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange, 
            this.onVariantChange.bind(this)
          );
        } else {
          document.addEventListener('variant:change', this.onVariantChange.bind(this));
        }
      }

      onMinusClick(event) {
        event.preventDefault();
        const currentValue = parseInt(this.input.value);
        const minValue = parseInt(this.input.min) || 1;
        const step = parseInt(this.input.step) || 1;
        
        if (currentValue > minValue) {
          this.updateQuantity(currentValue - step);
        }
      }

      onPlusClick(event) {
        event.preventDefault();
        const currentValue = parseInt(this.input.value);
        const maxValue = parseInt(this.input.max) || Infinity;
        const step = parseInt(this.input.step) || 1;
        
        if (currentValue < maxValue) {
          this.updateQuantity(currentValue + step);
        }
      }

      onInputChange(event) {
        const value = parseInt(this.input.value);
        this.updateQuantity(value);
      }

      updateQuantity(newValue) {
        // Garantir que o valor está dentro dos limites
        const minValue = parseInt(this.input.min) || 1;
        const maxValue = parseInt(this.input.max) || Infinity;
        const step = parseInt(this.input.step) || 1;
        
        newValue = Math.max(minValue, newValue);
        newValue = Math.min(maxValue, newValue);
        
        // Certificar que o valor seja múltiplo do step a partir do min
        const remainder = (newValue - minValue) % step;
        if (remainder !== 0) {
          newValue = newValue - remainder;
        }
        
        // Atualiza o valor no input
        this.input.value = newValue;
        
        // Atualiza o valor no formulário principal
        this.updateProductForm(newValue);
        
        // Dispara evento de mudança
        this.input.dispatchEvent(this.changeEvent);
        
        // Atualiza estado dos botões
        this.updateButtonState();
      }

      updateButtonState() {
        const currentValue = parseInt(this.input.value);
        const minValue = parseInt(this.input.min) || 1;
        const maxValue = parseInt(this.input.max) || Infinity;
        
        if (this.minusButton) {
          this.minusButton.disabled = currentValue <= minValue;
          this.minusButton.classList.toggle('disabled', currentValue <= minValue);
        }
        
        if (this.plusButton) {
          this.plusButton.disabled = currentValue >= maxValue;
          this.plusButton.classList.toggle('disabled', currentValue >= maxValue);
        }
      }
      
      // Sincroniza com outros seletores de quantidade na mesma seção
      syncWithOtherQuantitySelectors() {
        if (!this.sectionId || !this.input) return;
        
        // Encontrar todos os outros seletores com a mesma seção ID
        const allSelectors = document.querySelectorAll(`product-quantity-selector[data-section="${this.sectionId}"]`);
        if (allSelectors.length <= 1) return; // Não há outros seletores
        
        const thisInputId = this.input.id;
        
        // Adicionar um ouvinte de evento para mudanças neste input
        this.input.addEventListener('change', () => {
          const newValue = this.input.value;
          
          // Atualizar todos os outros seletores com o mesmo valor
          allSelectors.forEach(selector => {
            if (selector === this) return; // Ignorar a si mesmo
            
            const otherInput = selector.querySelector('input');
            if (otherInput && otherInput.id !== thisInputId && otherInput.value != newValue) {
              otherInput.value = newValue;
              
              // Atualizar o formulário associado ao outro seletor
              const otherInstance = selector;
              if (typeof otherInstance.updateProductForm === 'function') {
                otherInstance.updateProductForm(newValue);
              }
              
              // Atualizar o estado do botão do outro seletor
              if (typeof otherInstance.updateButtonState === 'function') {
                otherInstance.updateButtonState();
              }
            }
          });
        });
      }

      updateProductForm(newValue) {
        if (!this.productFormId) return;
        
        const form = document.getElementById(this.productFormId);
        if (!form) return;
        
        // Procura por um input hidden com name="quantity" ou cria um novo
        let quantityInput = form.querySelector('input[name="quantity"]');
        if (!quantityInput) {
          quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = 'quantity';
          form.appendChild(quantityInput);
        }
        
        // Só atualiza se o valor for diferente
        if (quantityInput.value != newValue) {
          // Atualiza o valor
          quantityInput.value = newValue;
          
          // Dispara um evento para notificar qualquer script que possa estar escutando
          const event = new Event('change', { bubbles: true });
          quantityInput.dispatchEvent(event);
        }
      }

      // Método chamado quando a variante do produto muda
      onVariantChange(event) {
        if (!event || !event.detail) return;
        
        const variant = event.detail.variant || (event.detail.data && event.detail.data.variant);
        
        if (!variant) return;
        
        // Redefine os limites de quantidade com base nas regras da nova variante
        if (this.input) {
          // Atualizar os atributos min, max e step
          if (variant.quantity_rule && variant.quantity_rule.min) {
            this.input.min = variant.quantity_rule.min;
            this.input.dataset.min = variant.quantity_rule.min;
          } else {
            this.input.min = 1;
            this.input.dataset.min = 1;
          }
          
          if (variant.quantity_rule && variant.quantity_rule.max) {
            this.input.max = variant.quantity_rule.max;
            this.input.dataset.max = variant.quantity_rule.max;
          } else {
            this.input.removeAttribute('max');
            delete this.input.dataset.max;
          }
          
          if (variant.quantity_rule && variant.quantity_rule.increment) {
            this.input.step = variant.quantity_rule.increment;
          } else {
            this.input.step = 1;
          }
          
          // Certificar-se de que o valor atual ainda é válido
          const minValue = parseInt(this.input.min) || 1;
          this.input.value = minValue;
          
          // Dispara um evento de mudança para atualizar outros elementos que possam estar observando
          this.input.dispatchEvent(this.changeEvent);
          
          // Atualiza o formulário com o novo valor
          this.updateProductForm(minValue);
          
          // Atualiza o estado visual dos botões
          this.updateButtonState();
          
          // Também atualiza os outros seletores de quantidade
          this.syncAllQuantitySelectors(minValue, variant);
        }
      }
      
      // Método para sincronizar todos os seletores de quantidade com as mesmas regras e valores
      syncAllQuantitySelectors(value, variant) {
        if (!this.sectionId) return;
        
        // Se este é o seletor primário, ele deve controlar os outros
        const isPrimarySelector = this.hasAttribute('data-primary-selector');
        const isFixedSelector = this.closest('[data-fixed-button="true"]');
        
        // Se for um seletor no botão fixo, não deve sincronizar os outros
        if (isFixedSelector && !isPrimarySelector) return;
        
        const allSelectors = document.querySelectorAll(`product-quantity-selector[data-section="${this.sectionId}"]`);
        
        allSelectors.forEach(selector => {
          // Ignorar este próprio seletor
          if (selector === this) return;
          
          // Se o seletor primário está atualizando, todos os outros devem seguir
          // Se um seletor não-primário está atualizando, só atualize os não-primários
          const otherIsPrimary = selector.hasAttribute('data-primary-selector');
          if (!isPrimarySelector && otherIsPrimary) return;
          
          const otherInput = selector.querySelector('input');
          if (otherInput) {
            // Atualizar regras de quantidade
            if (variant && variant.quantity_rule && variant.quantity_rule.min) {
              otherInput.min = variant.quantity_rule.min;
              otherInput.dataset.min = variant.quantity_rule.min;
            } else {
              otherInput.min = 1;
              otherInput.dataset.min = 1;
            }
            
            if (variant && variant.quantity_rule && variant.quantity_rule.max) {
              otherInput.max = variant.quantity_rule.max;
              otherInput.dataset.max = variant.quantity_rule.max;
            } else {
              otherInput.removeAttribute('max');
              delete otherInput.dataset.max;
            }
            
            if (variant && variant.quantity_rule && variant.quantity_rule.increment) {
              otherInput.step = variant.quantity_rule.increment;
            } else {
              otherInput.step = 1;
            }
            
            // Só atualiza se o valor for diferente, para evitar loops
            if (otherInput.value != value) {
              otherInput.value = value;
              
              // Atualizar formulário associado a este seletor
              const otherInstance = selector;
              if (typeof otherInstance.updateProductForm === 'function') {
                otherInstance.updateProductForm(value);
              }
              
              // Atualizar estado dos botões
              if (typeof otherInstance.updateButtonState === 'function') {
                otherInstance.updateButtonState();
              }
            }
          }
        });
      }

      disconnectedCallback() {
        // Remover event listeners quando o componente é destruído
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        } else {
          document.removeEventListener('variant:change', this.onVariantChange.bind(this));
        }
        
        if (this.minusButton) {
          this.minusButton.removeEventListener('click', this.onMinusClick.bind(this));
        }
        
        if (this.plusButton) {
          this.plusButton.removeEventListener('click', this.onPlusClick.bind(this));
        }
        
        if (this.input) {
          this.input.removeEventListener('change', this.onInputChange.bind(this));
        }
      }
    }
  );
}
